import { Badge, Code, Divider, Group, Pagination, Stack, Text, Title } from "@mantine/core";
import { Fragment, type ReactNode, useState } from "react";

import {
  CATEGORY_COLOR,
  CHANGELOG,
  type ChangelogCategory,
  type ChangelogRelease,
  type ChangelogTag,
  TAG_COLOR,
  UNRELEASED_LABEL,
} from "../rules/data";

/**
 * RELEASES_PER_PAGE is how many releases one page shows before paging begins, sized so a
 * page stays a comfortable scan.
 */
const RELEASES_PER_PAGE = 4;

/**
 * PAGE_COUNT is how many pages the full history spans at the current page size. It is
 * hoisted because the fixture history is fixed, so the count never changes between
 * renders.
 */
const PAGE_COUNT = Math.ceil(CHANGELOG.length / RELEASES_PER_PAGE);

/**
 * DIFF_MARKER maps each category to the single glyph that leads its change line, the way
 * a diff prefixes a hunk: a plus for additions, a tilde for refinements, a bang for
 * fixes, a minus for removals. The marker is tinted from `CATEGORY_COLOR` so a reader
 * reads the kind of a change from its prefix alone, without a separate category label.
 */
const DIFF_MARKER: Record<ChangelogCategory, string> = {
  Added: "+",
  Fixed: "!",
  Improved: "~",
  Removed: "-",
};

/**
 * CODE_SPAN matches a backtick-delimited span in a release line, capturing what sits
 * between the delimiters. Hoisted so the pattern is compiled once rather than per line.
 */
const CODE_SPAN = /`([^`]+)`/g;

/**
 * renderLine renders one authored release line, setting its backticked spans as inline
 * code. Release notes name enum values and wire forms, and an identifier set in prose
 * reads as a word rather than as something to paste, so the delimiters an author writes
 * become the code spans a reader sees.
 */
const renderLine = (text: string): ReactNode[] =>
  // Splitting on a capturing group alternates literal prose and captured span, so every
  // odd position is the content of a backticked span and every even position is prose.
  text
    .split(CODE_SPAN)
    .map((part, index) => (index % 2 === 0 ? part : <Code key={`${index}-${part}`}>{part}</Code>));

/**
 * formatDate renders an ISO date as a long month-day-year label in a fixed locale, so a
 * release date reads the same regardless of the visitor's locale. The fixed locale is
 * load-bearing: the page is prerendered then hydrated, so any locale- or clock-dependent
 * output here would diverge between the two passes and trip a hydration mismatch.
 */
const formatDate = (iso: string): string =>
  new Date(iso).toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" });

/**
 * releaseDate labels a release by the date it shipped, or as unreleased while it carries
 * none, so a release written up ahead of its tag never claims a day it has not shipped on.
 */
const releaseDate = (release: ChangelogRelease): string =>
  release.date === null ? UNRELEASED_LABEL : formatDate(release.date);

/**
 * releaseTags collects the distinct product tags a release touches across all its items,
 * in first-seen order, so a post header summarizes the surfaces a release affects without
 * repeating a tag the release changed more than once.
 */
const releaseTags = (release: ChangelogRelease): ChangelogTag[] => {
  const seen = new Set<ChangelogTag>();
  const tags: ChangelogTag[] = [];
  for (const item of release.items) {
    for (const tag of item.tags) {
      if (!seen.has(tag)) {
        seen.add(tag);
        tags.push(tag);
      }
    }
  }

  return tags;
};

/**
 * TagPills lays a release's distinct surfaces out as a row of product-colored pills,
 * tinted from `TAG_COLOR` so a reader can spot a surface by its hue, wrapping onto another
 * line when a release touches many.
 */
const TagPills = ({ release }: { release: ChangelogRelease }) => (
  <Group>
    {releaseTags(release).map((tag) => (
      <Badge color={TAG_COLOR[tag]} key={tag} tt="none">
        {tag}
      </Badge>
    ))}
  </Group>
);

/**
 * Changelog is the {% changelog %} renderer the documentation app injects. It reads each
 * release as a post: a dated header carrying the version and the surfaces it touched, a
 * lead sentence, then every change on its own line led by a category-colored diff marker.
 * It owns only the current page of history and renders a pager when the history spans more
 * than one page. The initial render is deterministic — first page, fixed-locale dates — so
 * the prerendered markup and the hydrated markup match. Releases read newest-first. It
 * draws no width container of its own, so it inherits the page's reading column and aligns
 * with the page heading above it.
 */
export const Changelog = () => {
  const [page, setPage] = useState(1);

  const start = (page - 1) * RELEASES_PER_PAGE;
  const releases = CHANGELOG.slice(start, start + RELEASES_PER_PAGE);

  return (
    <Stack gap="xl">
      <Stack gap={0}>
        {releases.map((release, index) => (
          <Fragment key={release.version}>
            <Stack component="article" gap="md">
              <Stack>
                <Group gap="sm">
                  <Text c="dimmed" fw={600} size="xs" tt="uppercase">
                    {releaseDate(release)}
                  </Text>
                  <TagPills release={release} />
                </Group>
                <Title order={3}>{release.title}</Title>
              </Stack>
              <Text size="md">{renderLine(release.summary)}</Text>
              <Stack gap="sm">
                {release.items.map((item) => (
                  <Group align="flex-start" gap="sm" key={item.text}>
                    <Text c={CATEGORY_COLOR[item.category]} ff="monospace" fw={700} lh="md" span>
                      {DIFF_MARKER[item.category]}
                    </Text>
                    <Text lh="md">{renderLine(item.text)}</Text>
                  </Group>
                ))}
              </Stack>
            </Stack>
            {index < releases.length - 1 ? <Divider my="xl" /> : null}
          </Fragment>
        ))}
      </Stack>

      {PAGE_COUNT > 1 ? (
        <Group justify="center" mt="xl">
          <Pagination onChange={setPage} total={PAGE_COUNT} value={page} />
        </Group>
      ) : null}
    </Stack>
  );
};
